import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../api';
import { GetCampaignFromEmailSequenceResponse } from '@dreamai/types';
import CampaignMessaging from './chat/canvas/dsl-components/CampaignMessaging';
import ErrorState from './common/ErrorState';

interface EmailSequenceApprovalViewProps {
  methodInput: {
    sequenceIdsToApprove: number[];
    sequenceIdsToReplace: number[];
    sendNewEmailsToCompletedProspects: boolean;
    continueReplacedSequenceForActiveProspects: boolean;
  };
}

const EmailSequenceApprovalView: React.FC<EmailSequenceApprovalViewProps> = ({
  methodInput,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCampaignId = async () => {
      try {
        const token = await getAccessTokenSilently();
        const emailSequenceId = methodInput.sequenceIdsToApprove[0];
        const response = await api.get<GetCampaignFromEmailSequenceResponse>(
          `/campaigns/email-sequence/${emailSequenceId}/campaign`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          setCampaignId(response.data.campaignId);
        } else {
          setError('Failed to load email sequence information');
        }
      } catch (err: any) {
        console.error('Error fetching campaign from email sequence:', err);
        setError(err.response?.data?.error || 'Failed to load email sequence');
      } finally {
        setLoading(false);
      }
    };

    fetchCampaignId();
  }, [methodInput.sequenceIdsToApprove, getAccessTokenSilently]);

  if (loading) {
    return (
      <div className='email-sequence-approval-view'>
        <div className='loading-container'>
          <div className='spinner'></div>
          <p>Loading email sequence...</p>
        </div>
      </div>
    );
  }

  if (error || campaignId === null) {
    return (
      <div className='email-sequence-approval-view'>
        <ErrorState
          title={error || 'Failed to load email sequence'}
          subtitle='Please try again later.'
        />
      </div>
    );
  }

  return (
    <div className='email-sequence-approval-view'>
      <CampaignMessaging
        campaignId={campaignId.toString()}
        sequenceIds={methodInput.sequenceIdsToApprove}
      />
    </div>
  );
};

export default EmailSequenceApprovalView;
